import type { ReactNode } from 'react';
import { rupee, pct } from '../lib/format';

/**
 * Page heading with an optional right-hand action slot.
 *
 * `display` sets the title in the display weight — reserved for the screens
 * that are a place you go to rather than a dialog you pass through, so the
 * app and the landing page read as the same product without every heading
 * turning into a magazine cover.
 */
/**
 * The page bar.
 *
 * Deliberately not a hero. The nav already says which page this is, so a
 * 30px title with a sentence of marketing copy under it was spending the
 * most valuable strip of the screen restating the tab you just clicked.
 * What belongs here is the page's title at reading size and, on the right,
 * the one action the page is for.
 *
 * `sub` is for live state that changes — "4 today · 2 still open" — not for
 * describing what the page is. It rides on the same line, so the table
 * starts within the first screenful instead of below a block of chrome.
 */
export function PageHead({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: ReactNode;
  action?: ReactNode;
  /** @deprecated The page bar is one size now; there is no display variant. */
  display?: boolean;
}) {
  return (
    <div className="page-bar">
      <h1>{title}</h1>
      {sub ? <span className="page-bar-sub">{sub}</span> : null}
      {action ? <div className="page-bar-action">{action}</div> : null}
    </div>
  );
}

export function Card({
  title,
  action,
  children,
  tight,
  footer,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** Removes body padding — for a card whose content is a table or list. */
  tight?: boolean;
  footer?: ReactNode;
}) {
  if (!title && !footer) {
    return <div className={tight ? 'card card-tight' : 'card'}>{children}</div>;
  }
  return (
    <div className="card card-tight">
      {title ? (
        <div className="card-head">
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {action}
        </div>
      ) : null}
      {tight ? children : <div className="card-body">{children}</div>}
      {footer ? <div className="card-foot">{footer}</div> : null}
    </div>
  );
}

export function Empty({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon ? <div className="empty-icon">{icon}</div> : null}
      <div className="t-md semibold" style={{ color: 'var(--text-2)' }}>
        {title}
      </div>
      {hint ? <div className="t-sm">{hint}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Spinner({ large }: { large?: boolean }) {
  return <div className={large ? 'spinner spinner-lg' : 'spinner'} role="status" aria-label="Loading" />;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty">
      <Spinner large />
      <div className="t-sm">{label}</div>
    </div>
  );
}

export function SkeletonRows({ rows = 4, height = 44 }: { rows?: number; height?: number }) {
  return (
    <div className="col" style={{ gap: 8, padding: 16 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <div className="banner banner-error" style={{ maxWidth: 420 }}>
        {message}
      </div>
      {onRetry ? (
        <button className="btn btn-sm mt-2" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** Underlined page tabs. */
export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: Array<{ value: T; label: string; count?: number }>;
}) {
  return (
    <div className="tabs" role="tablist">
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          aria-selected={value === it.value}
          className={`tab${value === it.value ? ' active' : ''}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
          {it.count != null ? <span className="tab-count num">{it.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Pill-style segmented control. */
export function Segment<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="segment" role="group">
      {items.map((it) => (
        <button
          key={it.value}
          className={value === it.value ? 'active' : ''}
          onClick={() => onChange(it.value)}
          aria-pressed={value === it.value}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A signed money figure with its own colour. `neutralZero` keeps an exactly-flat
 * number in body ink rather than painting ₹0.00 green, which reads as a win.
 */
export function Money({
  value,
  sign = true,
  decimals = 2,
  className = '',
  neutralZero = true,
}: {
  value: number;
  sign?: boolean;
  decimals?: number;
  className?: string;
  neutralZero?: boolean;
}) {
  const tone = neutralZero && value === 0 ? '' : value > 0 ? 'up' : value < 0 ? 'down' : '';
  return <span className={`num ${tone} ${className}`.trim()}>{rupee(value, { sign, decimals })}</span>;
}

/** "+₹1,240.50 (+1.24%)" — the pair a trader reads as one unit. */
export function PnlPair({
  value,
  fraction,
  className = '',
}: {
  value: number;
  fraction: number;
  className?: string;
}) {
  const tone = value === 0 ? '' : value > 0 ? 'up' : 'down';
  return (
    <span className={`num ${tone} ${className}`.trim()}>
      {rupee(value, { sign: true })} ({pct(fraction)})
    </span>
  );
}

/** Label/value pair, used down the side of detail cards. */
export function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-value num mt-1">{value}</div>
      {sub ? <div className="t-sm muted mt-1">{sub}</div> : null}
    </div>
  );
}

/** Compact key/value row for summary blocks. */
export function KV({ k, v, strong }: { k: ReactNode; v: ReactNode; strong?: boolean }) {
  return (
    <div className="between">
      <span className={strong ? 't-md semibold' : 't-md muted'}>{k}</span>
      <span className={`num ${strong ? 't-md semibold' : 't-md'}`}>{v}</span>
    </div>
  );
}
